import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { portfolioApi } from '@/api';
import { resolveFileUrl } from '@/api/client';
import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  LinkButton,
  LoadingState,
} from '@/components/ui';
import { VerifiedBadge } from '@/features/verification/VerifiedBadge';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { cn } from '@/utils/cn';
import { formatMonthYear, humanizeWorkMode } from '@/utils/format';

const ENTRY = 'border-line rounded-input flex gap-3 border p-3';
const ENTRY_ICON =
  'bg-brand-50 text-brand-600 ring-brand-100 grid size-9 shrink-0 place-items-center rounded-lg ring-1 ring-inset';

// Button.jsx only styles <button> and router <Link>; these are external
// anchors, so they borrow the same compact secondary/primary look.
const ANCHOR =
  'rounded-input inline-flex min-h-8 max-w-full items-center justify-center gap-1.5 px-3 py-1 ' +
  'text-sm font-medium whitespace-nowrap transition';
const ANCHOR_SECONDARY =
  'border-line bg-surface text-ink border shadow-xs hover:border-brand-300 hover:bg-brand-50/60 hover:text-brand-700';
const ANCHOR_PRIMARY = 'bg-brand-600 text-white shadow-sm hover:bg-brand-700';

const PROFICIENCY_LABELS = {
  BEGINNER: 'Beginner',
  INTERMEDIATE: 'Intermediate',
  ADVANCED: 'Advanced',
  EXPERT: 'Expert',
};

/** `FULL_TIME` -> "Full time": the enum as a person would write it. */
function sentenceCase(value) {
  const words = value.toLowerCase().replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function initials(name) {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}

function dateRange(from, to, isCurrent) {
  return `${formatMonthYear(from)} — ${isCurrent ? 'present' : formatMonthYear(to)}`;
}

/** Shared, not published: ask search engines to leave the page alone. */
function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);
}

function ExternalLink({ href, icon, children, primary = false }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className={cn(ANCHOR, primary ? ANCHOR_PRIMARY : ANCHOR_SECONDARY)}
    >
      <Icon name={icon} size={15} />
      {children}
    </a>
  );
}

/**
 * A student's shared portfolio, opened from a link they handed out. Readable
 * while signed out; the API decides what is on it and answers 404 for every
 * reason a page is unavailable, so this page cannot tell those apart either.
 */
export function PublicPortfolioPage() {
  const { slug } = useParams();
  useNoIndex();

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['public-portfolio', slug],
    queryFn: () => portfolioApi.getPublicPortfolio(slug),
    enabled: Boolean(slug),
    retry: (count, err) => err?.status !== 404 && count < 2,
  });

  if (isPending) return <LoadingState label="Loading portfolio…" />;
  if (isError) {
    if (error?.status === 404) return <Unavailable />;
    return (
      <div className="app-container py-10">
        <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <PortfolioView
      data={data}
      cvHref={data.cv ? portfolioApi.publicCvUrl(slug) : null}
      footer={<SharedFooter />}
    />
  );
}

/**
 * A student's profile laid out for reading: the shareable page, and the
 * student's own profile page when they are not editing it. `cvHref` adds a
 * download button, `actions` goes first among the header's buttons, and
 * `className` replaces the page frame when it sits inside another page.
 */
export function PortfolioView({
  data,
  cvHref = null,
  actions = null,
  footer = null,
  className = 'app-container max-w-5xl py-6 sm:py-8',
}) {
  const study = [data.degree, data.field_of_study].filter(Boolean).join(', ');
  const universityLine = [
    study,
    data.university_name,
    data.graduation_year ? `Class of ${data.graduation_year}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const links = [
    { key: 'portfolio', href: data.portfolio_url, icon: 'external', label: 'Portfolio' },
    { key: 'linkedin', href: data.linkedin_url, icon: 'linkedin', label: 'LinkedIn' },
    { key: 'github', href: data.github_url, icon: 'github', label: 'GitHub' },
  ].filter((link) => link.href);
  // Two columns only when both have something; otherwise the one that does
  // takes the full width instead of leaving half the page empty.
  const hasMain =
    Boolean(data.bio) ||
    Boolean(data.intro_video_url) ||
    [data.experience, data.projects, data.education].some((rows) => rows.length > 0);
  const hasSide = data.skills.length > 0 || data.certifications.length > 0;

  return (
    <div className={className}>
      {/* The card before the cover band.
      <header className="border-line bg-surface rounded-card shadow-card flex flex-col gap-4 border p-4 sm:flex-row sm:items-start sm:p-5">
        {data.profile_image_url ? (
          <img
            src={resolveFileUrl(data.profile_image_url)}
            alt={`Profile picture of ${data.full_name}`}
            className="border-line size-20 shrink-0 rounded-full border object-cover"
          />
        ) : (
          <span
            aria-hidden="true"
            className="bg-brand-50 text-brand-700 grid size-20 shrink-0 place-items-center rounded-full text-2xl font-semibold"
          >
            {initials(data.full_name)}
          </span>
        )}

        <div className="min-w-0 flex-1">
          <h1 className="text-ink text-xl font-semibold tracking-tight break-words sm:text-2xl">
            {data.full_name}
          </h1>
          {data.headline && <p className="text-ink mt-0.5 break-words">{data.headline}</p>}
          <div className="text-ink-muted mt-2 flex flex-col gap-1 text-sm">
            {universityLine && (
              <p className="inline-flex items-start gap-1.5">
                <Icon name="graduationCap" size={15} className="mt-0.5" />
                <span className="min-w-0 break-words">{universityLine}</span>
              </p>
            )}
            {data.location && (
              <p className="inline-flex items-start gap-1.5">
                <Icon name="target" size={15} className="mt-0.5" />
                <span className="min-w-0 break-words">{data.location}</span>
              </p>
            )}
          </div>

          {(links.length > 0 || data.cv) && (
            <div className="mt-4 flex flex-wrap gap-2">
              {data.cv && (
                <a
                  href={portfolioApi.publicCvUrl(slug)}
                  className={cn(ANCHOR, ANCHOR_PRIMARY)}
                  title={data.cv.file_name}
                >
                  <Icon name="download" size={15} />
                  Download CV
                </a>
              )}
              {links.map((link) => (
                <ExternalLink key={link.key} href={link.href} icon={link.icon}>
                  {link.label}
                </ExternalLink>
              ))}
            </div>
          )}
        </div>
      </header>
      */}
      {/* Profile-site style: a cover band, the picture across its edge. */}
      <header className="border-line bg-surface rounded-card shadow-card overflow-hidden border">
        <div aria-hidden="true" className="shell-hero h-24 sm:h-32" />
        <div className="px-4 pb-5 sm:px-6">
          {data.profile_image_url ? (
            <img
              src={resolveFileUrl(data.profile_image_url)}
              alt={`Profile picture of ${data.full_name}`}
              className="bg-surface -mt-12 size-24 rounded-full object-cover ring-4 ring-white sm:-mt-16 sm:size-32"
            />
          ) : (
            <span
              aria-hidden="true"
              className="bg-brand-50 text-brand-700 -mt-12 grid size-24 place-items-center rounded-full text-3xl font-semibold ring-4 ring-white sm:-mt-16 sm:size-32"
            >
              {initials(data.full_name)}
            </span>
          )}
          <h1 className="text-ink mt-3 text-xl font-semibold tracking-tight break-words sm:text-2xl">
            {data.full_name}
          </h1>
          {data.headline && <p className="text-ink mt-0.5 break-words">{data.headline}</p>}
          <div className="text-ink-muted mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
            {universityLine && (
              <p className="inline-flex items-start gap-1.5">
                <Icon name="graduationCap" size={15} className="mt-0.5" />
                <span className="min-w-0 break-words">{universityLine}</span>
              </p>
            )}
            {data.location && (
              <p className="inline-flex items-start gap-1.5">
                <Icon name="target" size={15} className="mt-0.5" />
                <span className="min-w-0 break-words">{data.location}</span>
              </p>
            )}
          </div>

          {(links.length > 0 || cvHref || actions) && (
            <div className="mt-4 flex flex-wrap gap-2">
              {actions}
              {cvHref && (
                <a href={cvHref} className={cn(ANCHOR, ANCHOR_PRIMARY)} title={data.cv?.file_name}>
                  <Icon name="download" size={15} />
                  Download CV
                </a>
              )}
              {links.map((link) => (
                <ExternalLink key={link.key} href={link.href} icon={link.icon}>
                  {link.label}
                </ExternalLink>
              ))}
            </div>
          )}
        </div>
      </header>

      <div
        className={cn(
          'mt-5 grid items-start gap-5',
          hasMain && hasSide && 'lg:grid-cols-[1fr_300px]',
        )}
      >
        {hasMain && (
          <div className="min-w-0 space-y-5">
            {data.bio && (
              <Card>
                <CardHeader title="About" />
                <p className="text-ink text-sm leading-relaxed break-words whitespace-pre-line">
                  {data.bio}
                </p>
              </Card>
            )}

            {data.intro_video_url && (
              <Card>
                <CardHeader title="Video presentation" />
                <video
                  src={resolveFileUrl(data.intro_video_url)}
                  controls
                  preload="metadata"
                  className="bg-night aspect-video w-full rounded-lg"
                >
                  Your browser cannot play this video.
                </video>
              </Card>
            )}

            {data.experience.length > 0 && (
              <Card>
                <CardHeader title="Experience" />
                <ol className="space-y-3">
                  {data.experience.map((row, index) => (
                    <li key={`${row.company_name}-${index}`} className={ENTRY}>
                      <span aria-hidden="true" className={ENTRY_ICON}>
                        <Icon name="briefcase" size={17} />
                      </span>
                      <div className="min-w-0">
                        <p className="text-ink leading-snug font-semibold break-words">
                          {row.title}
                        </p>
                        <p className="text-ink-muted mt-0.5 text-sm break-words">
                          {[
                            row.company_name,
                            row.employment_type && sentenceCase(row.employment_type),
                            row.location,
                            row.work_mode && humanizeWorkMode(row.work_mode),
                          ]
                            .filter(Boolean)
                            // "Remote · Remote" when the location is the mode.
                            .filter((part, index, parts) => parts.indexOf(part) === index)
                            .join(' · ')}
                        </p>
                        <p className="text-ink-muted mt-1 text-xs">
                          {dateRange(row.start_date, row.end_date, row.is_current)}
                        </p>
                        {row.description && (
                          <p className="text-ink-muted mt-2 text-sm leading-6 break-words whitespace-pre-line">
                            {row.description}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </Card>
            )}

            {data.projects.length > 0 && (
              <Card>
                <CardHeader title="Projects" />
                <ul className="grid gap-3 sm:grid-cols-2">
                  {data.projects.map((project, index) => (
                    <li
                      key={`${project.title}-${index}`}
                      className="border-line rounded-input flex min-w-0 flex-col border p-3"
                    >
                      <p className="text-ink font-semibold break-words">{project.title}</p>
                      {project.description && (
                        <p className="text-ink-muted mt-1 text-sm leading-6 break-words whitespace-pre-line">
                          {project.description}
                        </p>
                      )}
                      {(project.project_url || project.repository_url) && (
                        <div className="mt-auto flex flex-wrap gap-2 pt-3">
                          {project.project_url && (
                            <ExternalLink href={project.project_url} icon="external">
                              View live project
                            </ExternalLink>
                          )}
                          {project.repository_url && (
                            <ExternalLink href={project.repository_url} icon="github">
                              View source code
                            </ExternalLink>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {data.education.length > 0 && (
              <Card>
                <CardHeader title="Education" />
                <ol className="space-y-3">
                  {data.education.map((row, index) => (
                    <li key={`${row.institution_name}-${index}`} className={ENTRY}>
                      <span aria-hidden="true" className={ENTRY_ICON}>
                        <Icon name="university" size={17} />
                      </span>
                      <div className="min-w-0">
                        <p className="text-ink leading-snug font-semibold break-words">
                          {row.degree}
                          {row.field_of_study ? `, ${row.field_of_study}` : ''}
                        </p>
                        <p className="text-ink-muted mt-0.5 text-sm break-words">
                          {row.institution_name}
                          {row.grade ? ` · ${row.grade}` : ''}
                        </p>
                        <p className="text-ink-muted mt-1 text-xs">
                          {dateRange(row.start_date, row.end_date, row.is_current)}
                        </p>
                        {row.description && (
                          <p className="text-ink-muted mt-2 text-sm leading-6 break-words whitespace-pre-line">
                            {row.description}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </Card>
            )}
          </div>
        )}

        {hasSide && (
          <aside
            className={cn(
              'min-w-0',
              // Alone, skills and certifications sit side by side; one of
              // them on its own simply spans the page.
              !hasMain && data.skills.length > 0 && data.certifications.length > 0
                ? 'grid items-start gap-5 md:grid-cols-2'
                : 'space-y-5',
            )}
          >
            {data.skills.length > 0 && (
              <Card>
                <CardHeader title="Skills" />
                <ul className="flex flex-wrap gap-1.5" aria-label="Skills">
                  {data.skills.map((skill) => (
                    <li key={skill.name}>
                      <Badge tone="neutral">
                        {skill.name}
                        {skill.proficiency && (
                          <span className="opacity-70">
                            {PROFICIENCY_LABELS[skill.proficiency] ?? skill.proficiency}
                          </span>
                        )}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {data.certifications.length > 0 && (
              <Card>
                <CardHeader title="Certifications" />
                <ul className="divide-line divide-y">
                  {data.certifications.map((cert, index) => (
                    <li key={`${cert.name}-${index}`} className="py-2.5 first:pt-0 last:pb-0">
                      <p className="text-ink flex flex-wrap items-center gap-1.5 text-sm font-medium break-words">
                        {cert.name}
                        {cert.verified && <VerifiedBadge />}
                      </p>
                      {(cert.issuer || cert.issue_date) && (
                        <p className="text-ink-muted mt-0.5 text-xs">
                          {[cert.issuer, cert.issue_date && formatMonthYear(cert.issue_date)]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </aside>
        )}
      </div>

      {footer}
    </div>
  );
}

function SharedFooter() {
  return <p className="text-ink-muted mt-8 text-center text-xs">Shared from Join-Wonder</p>;
}

function Unavailable() {
  return (
    <div className="app-container max-w-xl py-12">
      <EmptyState
        icon={<Icon name="user" size={22} />}
        title="This portfolio isn't available"
        description="The link may have been turned off or replaced by its owner."
        action={
          <LinkButton to={paths.internships} variant="secondary" size="sm">
            Browse internships
          </LinkButton>
        }
      />
      <SharedFooter />
    </div>
  );
}
